// src/components/Colaborador/SistemaFeedback.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  doc,
  updateDoc,
} from 'firebase/firestore';

// Importações da biblioteca de datas e sua localização para o português
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from  "react-datepicker";
import ptBR from 'date-fns/locale/pt-BR';

// Registra a localidade para o calendário aparecer em Português
registerLocale('pt-BR', ptBR);


const COLLABORATORS_DOC_ID = "main_list";
// Paginação ajustada para 2 itens por página, conforme solicitado.
const ITEMS_PER_PAGE = 2;

// #region Componentes Reutilizáveis (Pagination, HistoricoList)
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    const pageNumbersToDisplay = [];
    const sideWidth = 1;
    const maxVisiblePageNumbers = (sideWidth * 2) + 3 + 2;
    if (totalPages <= maxVisiblePageNumbers) { for (let i = 1; i <= totalPages; i++) { pageNumbersToDisplay.push(i); } }
    else { const pages = new Set(); pages.add(1); pages.add(totalPages); for (let i = Math.max(1, currentPage - sideWidth); i <= Math.min(totalPages, currentPage + sideWidth); i++) { pages.add(i); } const sortedPages = Array.from(pages).sort((a, b) => a - b); let lastPageAdded = 0; for (const page of sortedPages) { if (lastPageAdded !== 0) { if (page - lastPageAdded > 2) { pageNumbersToDisplay.push('...'); } else if (page - lastPageAdded === 2) { pageNumbersToDisplay.push(lastPageAdded + 1); } } pageNumbersToDisplay.push(page); lastPageAdded = page; } }
    return (
      <nav>
        <ul className="pagination-wrapper">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>Anterior</button></li>
          {pageNumbersToDisplay.map((page, index) => { if (page === '...') { return <li key={`ellipsis-${index}`} className="page-item disabled"><span className="page-link">...</span></li>; } return (<li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}><button className="page-link" onClick={() => onPageChange(page)}>{page}</button></li>); })}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>Próxima</button></li>
        </ul>
      </nav>
    );
};


const HistoricoList = ({ titulo, items, loading, tipo, formatDisplayDate }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const getInitialDateRange = () => {
        const hoje = new Date();
        const inicioDoMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const fimDoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        return [inicioDoMes, fimDoMes];
    };
    const [dateRange, setDateRange] = useState(getInitialDateRange());
    const filteredItems = useMemo(() => {
        const [start, end] = dateRange;
        if (!start || !end) return items;
        const endDateWithTime = new Date(end);
        endDateWithTime.setHours(23, 59, 59, 999);
        return items.filter(item => {
            const itemDate = item.dataEnvio?.toDate() || item.dataSolicitacao?.toDate();
            if (!itemDate) return false;
            return itemDate >= start && itemDate <= endDateWithTime;
        });
    }, [items, dateRange]);
    useEffect(() => { setCurrentPage(1); }, [dateRange, items]);
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    const paginatedItems = useMemo(() => {
        const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
        const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
        return filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    }, [filteredItems, currentPage]);

    return (
        <div className="tab-pane-content">
            <h4 className="mb-3">{titulo}</h4>
            <div className="form-group mb-3">
                <label htmlFor="date-range-picker" className="form-label small">Filtrar por período:</label>
                <DatePicker id="date-range-picker" selectsRange={true} startDate={dateRange[0]} endDate={dateRange[1]} onChange={(update) => setDateRange(update)} isClearable={true} className="form-control form-control-sm" dateFormat="dd/MM/yyyy" locale="pt-BR" placeholderText="Clique para selecionar um período" />
            </div>

            {loading ? <p className="text-muted text-center py-3">Carregando...</p> : paginatedItems.length === 0 ? <p className="text-muted text-center py-3">Nenhum item encontrado.</p> : (
                <>
                    <ul className="list-group">
                        {paginatedItems.map(item => {
                            let itemClasses = 'list-group-item feedback-item';
                            if (tipo === 'solicitados') {
                                itemClasses += item.status === 'pendente' ? ' feedback-status-pendente' : ' feedback-status-respondido';
                            } else {
                                itemClasses += ` feedback-tipo-${item.tipo || 'geral'}`;
                            }

                            return (
                                <li key={item.id} className={itemClasses}>
                                    {tipo === 'recebidos' || tipo === 'enviados' ? (
                                        <>
                                            <p className="feedback-texto">"{item.texto}"</p>
                                            <small className="feedback-info">
                                                {tipo === 'recebidos' ? 'De: ' : 'Para: '}
                                                <strong>{tipo === 'recebidos' ? item.autorNome : item.destinatarioNome || 'Desconhecido'}</strong> <br/> Em: {formatDisplayDate(item.dataEnvio)}
                                            </small>
                                        </>
                                    ) : (
                                        <div className="d-flex justify-content-between align-items-center w-100">
                                            <div>
                                                <p className="mb-0">Solicitado para: <strong>{item.destinatarioNome || 'Desconhecido'}</strong></p>
                                                <small className="text-muted">Em: {formatDisplayDate(item.dataSolicitacao)}</small>
                                            </div>
                                            <span className={`badge ${item.status === 'pendente' ? 'bg-warning text-dark' : 'bg-success'}`}>{item.status === 'pendente' ? 'Pendente' : 'Respondido'}</span>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    <div className="mt-3">
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                    </div>
                </>
            )}
        </div>
    );
};
// #endregion


const SistemaFeedback = ({ currentUser }) => {
    const [usersList, setUsersList] = useState([]);
    const [destinatarioId, setDestinatarioId] = useState('');
    const [textoFeedback, setTextoFeedback] = useState('');
    const [tipoFeedback, setTipoFeedback] = useState('geral');
    const [textoRespostaSolicitacao, setTextoRespostaSolicitacao] = useState({});
    const [allFeedbacksRecebidos, setAllFeedbacksRecebidos] = useState([]);
    const [solicitacoesPendentes, setSolicitacoesPendentes] = useState([]);
    const [feedbacksEnviados, setFeedbacksEnviados] = useState([]);
    const [solicitacoesEnviadas, setSolicitacoesEnviadas] = useState([]);
    const [loading, setLoading] = useState({ users: true, feedbacks: false });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [abaAtiva, setAbaAtiva] = useState('enviar');

    // =================================================================================
    // CARREGAMENTO DE USUÁRIOS (MÉTODO main_list - CORRIGIDO E ESTABILIZADO)
    // =================================================================================
    useEffect(() => {
        const userId = currentUser?.uid;
        const empresaId = currentUser?.empresa_id;

        if (!userId || !empresaId) {
            setLoading(prev => ({...prev, users: false}));
            return;
        }

        setLoading(prev => ({...prev, users: true}));
        const collaboratorsDocRef = doc(db, 'Empresas', empresaId, 'colaboradores', COLLABORATORS_DOC_ID);

        const unsubscribe = onSnapshot(collaboratorsDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const colaboradoresArray = docSnap.data().colaboradores; 
                if (Array.isArray(colaboradoresArray)) {
                    const users = colaboradoresArray
                        .map(colaborador => ({
                            id: colaborador.id || colaborador.uid, 
                            nome: colaborador.nome,
                            email: colaborador.email,
                        }))
                        .filter(user => user.id && user.id !== userId) 
                        .sort((a, b) => (a.nome || '').localeCompare(b.nome || '')); 
                    setUsersList(users);
                } else {
                    setError("Formato de dados de colaboradores inválido.");
                    setUsersList([]);
                }
            } else {
                setError("Documento da lista de colaboradores não encontrado.");
                setUsersList([]);
            }
            setLoading(prev => ({...prev, users: false}));
        }, (err) => {
            console.error("Erro ao buscar lista de colaboradores:", err);
            setError("Não foi possível carregar a lista de colaboradores.");
            setLoading(prev => ({...prev, users: false}));
            setUsersList([]);
        });

        return () => unsubscribe();
    // Dependência estável: O efeito só roda novamente se o ID do usuário ou da empresa mudar.
    }, [currentUser?.uid, currentUser?.empresa_id]);


    useEffect(() => {
        if (!currentUser?.uid || !currentUser?.empresa_id) return;

        setLoading(prev => ({ ...prev, feedbacks: true }));
        const feedbacksRef = collection(db, 'Empresas', currentUser.empresa_id, 'feedbacks');
        
        const unsubs = [];
        const commonErrorHandler = (type, err) => console.error(`Erro ao buscar ${type}:`, err);
    
        unsubs.push(onSnapshot(query(feedbacksRef, where('destinatarioId', '==', currentUser.uid), where('tipo', 'in', ['geral', 'positivo', 'construtivo', 'resposta_solicitacao']), orderBy('dataEnvio', 'desc')), snap => setAllFeedbacksRecebidos(snap.docs.map(d => ({ id: d.id, ...d.data() }))), err => commonErrorHandler('feedbacks recebidos', err)));
        unsubs.push(onSnapshot(query(feedbacksRef, where('destinatarioId', '==', currentUser.uid), where('tipo', '==', 'solicitacao'), where('status', '==', 'pendente'), orderBy('dataSolicitacao', 'desc')), snap => setSolicitacoesPendentes(snap.docs.map(d => ({ id: d.id, ...d.data() }))), err => commonErrorHandler('solicitações pendentes', err)));
        unsubs.push(onSnapshot(query(feedbacksRef, where('autorId', '==', currentUser.uid), where('tipo', 'in', ['geral', 'positivo', 'construtivo', 'resposta_solicitacao']), orderBy('dataEnvio', 'desc')), snap => setFeedbacksEnviados(snap.docs.map(d => ({ id: d.id, ...d.data() }))), err => commonErrorHandler('feedbacks enviados', err)));
        unsubs.push(onSnapshot(query(feedbacksRef, where('solicitanteId', '==', currentUser.uid), where('tipo', '==', 'solicitacao'), orderBy('dataSolicitacao', 'desc')), snap => setSolicitacoesEnviadas(snap.docs.map(d => ({ id: d.id, ...d.data() }))), err => commonErrorHandler('solicitações enviadas', err)));
    
        Promise.allSettled(unsubs).finally(() => setLoading(prev => ({ ...prev, feedbacks: false })));
    
        return () => unsubs.forEach(unsub => unsub());
      }, [currentUser?.uid, currentUser?.empresa_id]);

    const limparMensagens = (clearSuccess = true, clearError = true) => { if (clearError) setError(''); if (clearSuccess) setSuccess(''); };
    const handleAbaChange = (novaAba) => { setAbaAtiva(novaAba); limparMensagens(); setDestinatarioId(''); setTextoFeedback(''); };
    const formatDisplayDate = (dateSource) => { if (!dateSource) return 'N/D'; try { const date = dateSource.seconds ? new Date(dateSource.seconds * 1000) : new Date(dateSource); if (isNaN(date.getTime())) return 'Data inválida'; return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return 'Erro na data'; } };
    
    // As funções de criação de feedback incluem o campo `lido: false` para o sino de notificações
    const handleEnviarFeedback = async (e) => {
        e.preventDefault();
        const destInfo = usersList.find(u => u.id === destinatarioId);
        if (!destinatarioId || !textoFeedback.trim() || !destInfo) {  setError("Selecione um destinatário e escreva uma mensagem."); return; }
        try {
            await addDoc(collection(db, 'Empresas', currentUser.empresa_id, 'feedbacks'), {
                autorId: currentUser.uid, autorNome: currentUser.displayName,
                destinatarioId: destinatarioId, destinatarioNome: destInfo.nome,
                texto: textoFeedback, dataEnvio: Timestamp.now(),
                tipo: tipoFeedback, status: 'enviado', lido: false
            });
            setSuccess("Feedback enviado com sucesso!");
            setTextoFeedback(''); setDestinatarioId(''); setTipoFeedback('geral');
        } catch (err) { setError(`Erro ao enviar feedback: ${err.message}`); }
    };

    const handleSolicitarFeedback = async (e) => {
        e.preventDefault();
        const destInfo = usersList.find(u => u.id === destinatarioId);
        if (!destinatarioId || !destInfo) { setError("Selecione um colaborador."); return; }
        try {
            await addDoc(collection(db, 'Empresas', currentUser.empresa_id, 'feedbacks'), {
                solicitanteId: currentUser.uid, solicitanteNome: currentUser.displayName,
                destinatarioId: destinatarioId, destinatarioNome: destInfo.nome,
                dataSolicitacao: Timestamp.now(), tipo: 'solicitacao', status: 'pendente', lido: false
            });
            setSuccess("Solicitação enviada com sucesso!");
            setDestinatarioId('');
        } catch (err) { setError(`Erro ao solicitar feedback: ${err.message}`); }
    };
    
    const handleEnviarRespostaSolicitacao = async (e, sol) => {
        e.preventDefault();
        const textoResposta = textoRespostaSolicitacao[sol.id]?.trim();
        if(!textoResposta) { setError(`Escreva o feedback para ${sol.solicitanteNome}.`); return; }
        try {
            const feedbacksRef = collection(db, 'Empresas', currentUser.empresa_id, 'feedbacks');
            await addDoc(feedbacksRef, {
                autorId: currentUser.uid, autorNome: currentUser.displayName,
                destinatarioId: sol.solicitanteId, destinatarioNome: sol.solicitanteNome,
                texto: textoResposta, dataEnvio: Timestamp.now(),
                tipo: 'resposta_solicitacao', solicitacaoId: sol.id, status: 'respondido', lido: false
            });
            await updateDoc(doc(feedbacksRef, sol.id), { status: 'respondido', dataResposta: Timestamp.now() });
            setSuccess("Resposta enviada!");
            setTextoRespostaSolicitacao(prev => ({...prev, [sol.id]: ''}));
        } catch (err) { setError(`Erro ao enviar resposta: ${err.message}`); }
    };

    return (
        <div className="sistema-feedback-container">
            {error && <div className="alert alert-danger">{error.split('\n').filter(i=>i).map((item, i) => <p key={i} className="mb-0">{item}</p>)}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <ul className="nav nav-tabs">
                <li className="nav-item"><button className={`nav-link ${abaAtiva === 'enviar' ? 'active' : ''}`} onClick={() => handleAbaChange('enviar')}>Enviar</button></li>
                <li className="nav-item"><button className={`nav-link ${abaAtiva === 'solicitar' ? 'active' : ''}`} onClick={() => handleAbaChange('solicitar')}>Solicitar</button></li>
                <li className="nav-item"><button className={`nav-link ${abaAtiva === 'recebidos' ? 'active' : ''}`} onClick={() => handleAbaChange('recebidos')}>Recebidos <span className="badge bg-secondary ms-1">{allFeedbacksRecebidos.length}</span></button></li>
                <li className="nav-item"><button className={`nav-link ${abaAtiva === 'enviados' ? 'active' : ''}`} onClick={() => handleAbaChange('enviados')}>Enviados <span className="badge bg-info text-dark ms-1">{feedbacksEnviados.length}</span></button></li>
                <li className="nav-item"><button className={`nav-link ${abaAtiva === 'solicitados' ? 'active' : ''}`} onClick={() => handleAbaChange('solicitados')}>Solicitados <span className="badge bg-info text-dark ms-1">{solicitacoesEnviadas.length}</span></button></li>
                <li className="nav-item"><button className={`nav-link ${abaAtiva === 'pendentes' ? 'active' : ''}`} onClick={() => handleAbaChange('pendentes')}>Pendentes <span className="badge bg-warning text-dark ms-1">{solicitacoesPendentes.length}</span></button></li>
            </ul>

            <div className="tab-content-wrapper">
                {abaAtiva === 'enviar' && (
                  <div className="tab-pane-content">
                    <form onSubmit={handleEnviarFeedback} className="feedback-form">
                      <h4 className="mb-4">Enviar Feedback</h4>
                      <div className="form-group mb-3"><label htmlFor="destinatarioEnviar" className="form-label">Para:</label><select id="destinatarioEnviar" className="form-select" value={destinatarioId} onChange={(e) => setDestinatarioId(e.target.value)} required disabled={loading.users}><option value="">{loading.users ? 'Carregando...' : 'Selecione um colaborador'}</option>{usersList.map(user => (<option key={user.id} value={user.id}>{user.nome || user.email}</option>))}</select></div>
                      <div className="form-group mb-3"><label htmlFor="tipoFeedback" className="form-label">Tipo de Feedback:</label><select id="tipoFeedback" className="form-select" value={tipoFeedback} onChange={(e) => setTipoFeedback(e.target.value)}><option value="geral">Geral</option><option value="positivo">Positivo 👍</option><option value="construtivo">Construtivo 💡</option></select></div>
                      <div className="form-group mb-3"><label htmlFor="textoFeedbackEnviar" className="form-label">Mensagem:</label><textarea id="textoFeedbackEnviar" className="form-control" value={textoFeedback} onChange={(e) => setTextoFeedback(e.target.value)} required rows="4" placeholder="Escreva seu feedback aqui..."></textarea></div>
                      <button type="submit" className="btn btn-primary w-100" disabled={loading.users || !destinatarioId || !textoFeedback.trim()}>Enviar Feedback</button>
                    </form>
                  </div>
                )}
                {abaAtiva === 'solicitar' && (
                  <div className="tab-pane-content">
                    <form onSubmit={handleSolicitarFeedback} className="feedback-form">
                      <h4 className="mb-4">Solicitar Feedback</h4>
                      <div className="form-group mb-3"><label htmlFor="destinatarioSolicitar" className="form-label">Solicitar De:</label><select id="destinatarioSolicitar" className="form-select" value={destinatarioId} onChange={(e) => setDestinatarioId(e.target.value)} required disabled={loading.users}><option value="">{loading.users ? 'Carregando...' : 'Selecione um colaborador'}</option>{usersList.map(user => (<option key={user.id} value={user.id}>{user.nome || user.email}</option>))}</select></div>
                      <p className="form-text text-muted small mb-3">Uma notificação será enviada ao colaborador selecionado.</p>
                      <button type="submit" className="btn btn-info w-100" disabled={loading.users || !destinatarioId}>Solicitar</button>
                    </form>
                  </div>
                )}
                
                {abaAtiva === 'recebidos' && <HistoricoList titulo="Feedbacks Recebidos" items={allFeedbacksRecebidos} loading={loading.feedbacks} tipo="recebidos" formatDisplayDate={formatDisplayDate} />}
                {abaAtiva === 'enviados' && <HistoricoList titulo="Histórico de Feedbacks Enviados" items={feedbacksEnviados} loading={loading.feedbacks} tipo="enviados" formatDisplayDate={formatDisplayDate} />}
                {abaAtiva === 'solicitados' && <HistoricoList titulo="Histórico de Solicitações Feitas" items={solicitacoesEnviadas} loading={loading.feedbacks} tipo="solicitados" formatDisplayDate={formatDisplayDate} />}
                
                {abaAtiva === 'pendentes' && (
                  <div className="tab-pane-content">
                    <h4 className="mb-3">Solicitações Para Você Responder</h4>
                    {loading.feedbacks && solicitacoesPendentes.length === 0 ? <p className="text-muted text-center py-3">Carregando...</p> : solicitacoesPendentes.length === 0 ? <p className="text-muted text-center py-3">Você não tem solicitações pendentes.</p> : (
                      <ul className="list-group">
                        {solicitacoesPendentes.map(sol => (
                          <li key={sol.id} className="list-group-item">
                            <p className="mb-1"><strong>{sol.solicitanteNome || 'Desconhecido'}</strong> solicitou seu feedback.</p>
                            <small className="text-muted">Em: {formatDisplayDate(sol.dataSolicitacao)}</small>
                            <form onSubmit={(e) => handleEnviarRespostaSolicitacao(e, sol)} className="responder-solicitacao-form mt-2">
                              <div className="form-group"><textarea className="form-control form-control-sm" value={textoRespostaSolicitacao[sol.id] || ''} onChange={(e) => setTextoRespostaSolicitacao(prev => ({ ...prev, [sol.id]: e.target.value }))} required rows="3" placeholder={`Escreva seu feedback para ${sol.solicitanteNome}...`}></textarea></div>
                              <button type="submit" className="btn btn-primary btn-sm w-100 mt-2" disabled={!textoRespostaSolicitacao[sol.id] || !textoRespostaSolicitacao[sol.id].trim()}>Enviar Resposta</button>
                            </form>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
            </div>
        </div>
    );
};

export default SistemaFeedback;